import { RateSelect, useMyRates } from '@/components/rates';
import { BillingCard, LockBanner, RepairChargesCard, useUnlock } from '@/components/billing';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Eye, EyeOff, KeyRound, Package, Plus, Printer, Send, Trash2, XCircle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AppointmentsCard } from '@/components/appointments';
import { ChecklistsCard } from '@/components/checklists';
import { PrintButton, PublicStatusCard } from '@/components/printing';
import { centsToInput, DOCUMENTS, DocumentsMenu, formatCents, OUTCOMES, parseEuro, REPAIR_STATUS, RepairStatusBadge, WARRANTIES } from '@/components/repairs';
import { formatBytes, formatMinutes, Tabs, UploadButton } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Checkbox, CollapsibleCards, Input, Label, Select, Skeleton, Textarea } from '@/components/ui/primitives';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DEFAULT_QUOTE_UNIT, formatQuantity, QUOTE_KIND_LABELS, QUOTE_UNITS, type PartStatus, type QuoteItem, type RepairDetail, type RepairOutcome, type RepairStatus, type TicketMeta } from '@/lib/types';
import { cn, formatDate, formatDateTime, formatRelative } from '@/lib/utils';

const STEP_KINDS = { WORK: 'Arbeitsschritt', DIAGNOSIS: 'Diagnose', TEST: 'Prüfung', NOTE: 'Notiz' } as const;
const PART_STATUS: Record<PartStatus, string> = { NEEDED: 'benötigt', ORDERED: 'bestellt', RECEIVED: 'eingetroffen', INSTALLED: 'verbaut' };
const CHANNELS = { PORTAL: 'Kundenportal', PHONE: 'telefonisch', IN_PERSON: 'persönlich', EMAIL: 'per E-Mail' } as const;

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function describe(type: string, d: Record<string, unknown> | null): string {
  const data = d ?? {};
  const st = (s: unknown) => REPAIR_STATUS[s as RepairStatus]?.label ?? String(s);
  switch (type) {
    case 'received':
      return 'hat das Gerät angenommen';
    case 'status_changed':
      return `hat den Status auf „${st(data.to)}“ gesetzt${data.outcome ? ` (${OUTCOMES[data.outcome as RepairOutcome]})` : ''}`;
    case 'technician_changed':
      return data.technicianId ? 'hat den Techniker geändert' : 'hat den Techniker entfernt';
    case 'updated':
      return 'hat Reparaturdaten geändert';
    case 'part_added':
      return `hat das Teil „${data.description}“ erfasst`;
    case 'part_status':
      return `hat „${data.description}“ als ${PART_STATUS[data.to as PartStatus]} markiert`;
    case 'quote_created':
      return `hat Kostenvoranschlag Fassung ${data.version} begonnen`;
    case 'quote_sent':
      return `hat den Kostenvoranschlag (${formatCents(Number(data.gross))}) gesendet`;
    case 'quote_approved':
      return `Kostenvoranschlag freigegeben (${CHANNELS[data.channel as keyof typeof CHANNELS] ?? data.channel})`;
    case 'quote_rejected':
      return `Kostenvoranschlag abgelehnt (${CHANNELS[data.channel as keyof typeof CHANNELS] ?? data.channel})`;
    case 'attachment_added':
      return `hat „${data.name}“ hochgeladen`;
    case 'checklist_added':
      return `hat die Checkliste „${data.form}“ ausgefüllt`;
    case 'appointment_scheduled':
      return `hat den Termin „${data.title}“ am ${new Date(String(data.startAt)).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })} geplant`;
    case 'appointment_rescheduled':
      return `hat den Termin „${data.title}“ verschoben`;
    case 'appointment_cancelled':
      return `hat den Termin „${data.title}“ abgesagt`;
    case 'picked_up':
      return `Abholung durch ${data.pickedUpBy} dokumentiert`;
    case 'public_access_reset':
      return 'hat eine neue PIN für die Statusabfrage erzeugt';
    case 'charge_added':
      return `hat die Pauschale „${data.name}“ (${formatCents(Number(data.priceCents))}) erfasst`;
    case 'charge_removed':
      return `hat die Pauschale „${data.name}“ entfernt`;
    case 'billing_released':
      return `hat den Vorgang zur Abrechnung freigegeben${data.note ? ` („${data.note}“)` : ''}`;
    case 'billing_withdrawn':
      return `hat die Abrechnungsfreigabe zurückgenommen${data.note ? ` (${data.note})` : ''}`;
    case 'billing_returned':
      return `hat den Vorgang mit Rückfrage zurückgegeben: „${data.note}“`;
    case 'billing_transferred':
      return `hat den Vorgang in Powerbird übernommen${data.reference ? ` (Beleg ${data.reference})` : ''}`;
    case 'billing_not_billable':
      return `hat den Vorgang ohne Rechnung abgeschlossen${data.note ? ` (${data.note})` : ''}`;
    case 'billing_change_requested':
      return `hat eine Änderung beantragt: „${data.note}“`;
    case 'billing_change_approved':
      return `hat den Änderungsantrag genehmigt${data.note ? ` (${data.note})` : ''} – wieder bearbeitbar`;
    case 'billing_change_rejected':
      return `hat den Änderungsantrag abgelehnt${data.note ? `: ${data.note}` : ''}`;
    case 'billing_reopened':
      return 'hat die Abrechnung wieder geöffnet';
    case 'cost_exceeded': {
      const ref = data.reference as { kind: string; net?: number; gross?: number } | undefined;
      return ref?.kind === 'quote'
        ? `Aufwand (${formatCents(Number(data.actualNet))} netto) übersteigt den Kostenvoranschlag (${formatCents(Number(ref.net))} netto) – Techniker benachrichtigt`
        : `Aufwand (${formatCents(Number(data.actualGross))} brutto) übersteigt die Kostenfreigabe (${formatCents(Number(ref?.gross))} brutto) – Techniker benachrichtigt`;
    }
    default:
      return type;
  }
}

function StepsTab({ r, canWork, onChange }: { r: RepairDetail; canWork: boolean; onChange: () => void }) {
  const { me } = useAuth();
  const [kind, setKind] = useState<keyof typeof STEP_KINDS>('WORK');
  const [text, setText] = useState('');
  const [minutes, setMinutes] = useState('');
  const [rateId, setRateId] = useState('');
  const [result, setResult] = useState<'PASSED' | 'FAILED'>('PASSED');
  const [visible, setVisible] = useState(true);
  const [flatRateId, setFlatRateId] = useState('');
  const { data: flatData } = useQuery({ queryKey: ['repair-flat-rates'], queryFn: () => api<{ items: { id: string; name: string; priceCents: number; active: boolean }[] }>('/repair-flat-rates'), enabled: canWork });
  const flats = (flatData?.items ?? []).filter((x) => x.active);
  // Freitexte nur noch bei älteren Reparaturen, die schon welche haben – sonst entsteht der Bericht aus den Arbeitsschritten
  const legacyTexts = Boolean(r.diagnosis || r.report);
  const [diagnosis, setDiagnosis] = useState(r.diagnosis ?? '');
  const [report, setReport] = useState(r.report ?? '');
  useEffect(() => {
    setDiagnosis(r.diagnosis ?? '');
    setReport(r.report ?? '');
  }, [r.diagnosis, r.report]);

  const add = useMutation({
    mutationFn: () =>
      api<{ id: string; costWarning: string | null }>(`/repairs/${r.id}/steps`, {
        method: 'POST',
        body:
          kind === 'TEST'
            ? { kind, text, testResult: result, flatRateId: flatRateId || null, customerVisible: visible }
            : { kind, text, minutes: Number(minutes) || null, rateId: Number(minutes) > 0 ? rateId || null : null, customerVisible: kind !== 'NOTE' && visible },
      }),
    onSuccess: (res) => {
      setText('');
      setMinutes('');
      setFlatRateId('');
      if (res.costWarning) toast.warning(res.costWarning, { duration: 15_000 });
      onChange();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({ mutationFn: (id: string) => api(`/repairs/${r.id}/steps/${id}`, { method: 'DELETE' }), onSuccess: onChange, onError: (e) => toast.error(errorMessage(e)) });
  const saveTexts = useMutation({
    mutationFn: () => api(`/repairs/${r.id}`, { method: 'PATCH', body: { diagnosis, report } }),
    onSuccess: () => {
      toast.success('Gespeichert');
      onChange();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const dirty = diagnosis !== (r.diagnosis ?? '') || report !== (r.report ?? '');

  return (
    <div className="grid gap-4">
      {r.costs.exceeded && <CostStatusInfo r={r} />}
      {legacyTexts && (
        <Card>
          <CardContent className="grid gap-4 pt-5">
            <p className="text-xs text-muted-foreground">Freitext aus einer früheren Version – neue Einträge bitte unten als Diagnose bzw. Arbeitsschritt erfassen.</p>
            <Field label="Diagnose" htmlFor="rd-diag" hint="Erscheint im Reparaturbericht für den Kunden.">
              <Textarea id="rd-diag" rows={3} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} disabled={!canWork} />
            </Field>
            <Field label="Durchgeführte Arbeiten (Bericht)" htmlFor="rd-report">
              <Textarea id="rd-report" rows={4} value={report} onChange={(e) => setReport(e.target.value)} disabled={!canWork} />
            </Field>
            {canWork && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => saveTexts.mutate()} disabled={!dirty} loading={saveTexts.isPending}>
                  Speichern
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm">Diagnose, Arbeitsschritte und Prüfungen</CardTitle>
          <span className="text-sm text-muted-foreground">{formatMinutes(r.totalMinutes)} gebucht</span>
        </CardHeader>
        <CardContent className="grid gap-2">
          <p className="text-xs text-muted-foreground">Der Reparaturbericht für den Kunden entsteht aus den Einträgen mit „im Reparaturbericht“: Diagnose, durchgeführte Arbeiten und Prüfungen.</p>
          {r.steps.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Einträge.</p>}
          {r.steps.map((s) => (
            <div key={s.id} className={cn('rounded-lg border p-3 text-sm', s.kind === 'NOTE' && 'bg-amber-50/70 dark:bg-amber-950/20')}>
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">{STEP_KINDS[s.kind]}</Badge>
                {s.testResult && (
                  <Badge variant={s.testResult === 'PASSED' ? 'success' : 'destructive'}>{s.testResult === 'PASSED' ? 'bestanden' : 'nicht bestanden'}</Badge>
                )}
                {s.minutes && <span>{formatMinutes(s.minutes)}{s.rateName ? ` · ${s.rateName}` : ''}</span>}
                {s.flatName && <span>Pauschale {s.flatName} ({formatCents(s.flatCents ?? 0)})</span>}
                <span>
                  {s.authorName} · {formatRelative(s.createdAt)}
                </span>
                {s.customerVisible ? <Eye className="size-3.5" aria-label="im Bericht sichtbar" /> : <EyeOff className="size-3.5" aria-label="nur intern" />}
                {canWork && s.authorId === me?.user.id && (
                  <button className="ml-auto hover:text-destructive" onClick={() => del.mutate(s.id)} aria-label="Eintrag löschen">
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>
              <p className="whitespace-pre-wrap">{s.text}</p>
            </div>
          ))}
          {canWork && (
            <form
              className="mt-2 grid gap-2 rounded-lg border p-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (text.trim()) add.mutate();
              }}
            >
              <div className="grid gap-2 sm:grid-cols-[10rem_9rem_1fr]">
                <Select value={kind} onChange={(e) => setKind(e.target.value as keyof typeof STEP_KINDS)} aria-label="Art">
                  {Object.entries(STEP_KINDS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
                {kind === 'TEST' ? (
                  <>
                    <Select value={result} onChange={(e) => setResult(e.target.value as 'PASSED' | 'FAILED')} aria-label="Ergebnis">
                      <option value="PASSED">bestanden</option>
                      <option value="FAILED">nicht bestanden</option>
                    </Select>
                    {/* Prüfungen sind Pauschalleistungen – keine Minuten */}
                    <Select value={flatRateId} onChange={(e) => setFlatRateId(e.target.value)} aria-label="Prüfpauschale">
                      <option value="">ohne Berechnung</option>
                      {flats.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name} ({formatCents(f.priceCents)})
                        </option>
                      ))}
                    </Select>
                  </>
                ) : (
                  <>
                    <Input type="number" min={1} placeholder="Minuten" value={minutes} onChange={(e) => setMinutes(e.target.value)} aria-label="Minuten" />
                    {Number(minutes) > 0 && <RateSelect value={rateId} onChange={setRateId} />}
                  </>
                )}
              </div>
              <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Was wurde gemacht oder festgestellt?" aria-label="Beschreibung" />
              <div className="flex flex-wrap items-center justify-between gap-2">
                {kind !== 'NOTE' ? (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={visible} onCheckedChange={(v) => setVisible(v === true)} /> im Reparaturbericht für den Kunden
                  </label>
                ) : (
                  <span className="text-xs text-muted-foreground">Notizen sind nur intern sichtbar.</span>
                )}
                <Button type="submit" size="sm" disabled={!text.trim()} loading={add.isPending}>
                  Erfassen
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PartsTab({ r, canWork, onChange }: { r: RepairDetail; canWork: boolean; onChange: () => void }) {
  const [f, setF] = useState({ description: '', partNumber: '', supplier: '', quantity: '1', price: '' });
  const onError = (e: unknown) => toast.error(errorMessage(e));
  const add = useMutation({
    mutationFn: () =>
      api<{ id: string; costWarning: string | null }>(`/repairs/${r.id}/parts`, {
        method: 'POST',
        body: { description: f.description, partNumber: f.partNumber || null, supplier: f.supplier || null, quantity: Number(f.quantity) || 1, unitPriceCents: parseEuro(f.price) ?? 0 },
      }),
    onSuccess: (res) => {
      setF({ description: '', partNumber: '', supplier: '', quantity: '1', price: '' });
      if (res.costWarning) toast.warning(res.costWarning, { duration: 15_000 });
      onChange();
    },
    onError,
  });
  const update = useMutation({
    mutationFn: ({ part, status }: { part: RepairDetail['parts'][number]; status: PartStatus }) =>
      api(`/repairs/${r.id}/parts/${part.id}`, { method: 'PUT', body: { ...part, status } }),
    onSuccess: onChange,
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api(`/repairs/${r.id}/parts/${id}`, { method: 'DELETE' }), onSuccess: onChange, onError });
  const total = r.parts.reduce((s, p) => s + p.quantity * p.unitPriceCents, 0);

  return (
    <Card>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Teil</TH>
            <TH className="hidden sm:table-cell">Lieferant</TH>
            <TH className="text-right">Menge</TH>
            <TH className="hidden text-right md:table-cell">EK/Stück</TH>
            <TH>Status</TH>
            <TH className="w-10" />
          </TR>
        </THead>
        <TBody>
          {r.parts.length === 0 && (
            <TR>
              <TD colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                Keine Ersatzteile erfasst.
              </TD>
            </TR>
          )}
          {r.parts.map((p) => (
            <TR key={p.id}>
              <TD>
                <p className="font-medium">{p.description}</p>
                {p.partNumber && <p className="font-mono text-xs text-muted-foreground">{p.partNumber}</p>}
              </TD>
              <TD className="hidden text-muted-foreground sm:table-cell">{p.supplier ?? '–'}</TD>
              <TD className="text-right tabular-nums">{p.quantity}</TD>
              <TD className="hidden text-right tabular-nums md:table-cell">{formatCents(p.unitPriceCents)}</TD>
              <TD>
                <Select className="h-8 w-36" value={p.status} disabled={!canWork} onChange={(e) => update.mutate({ part: p, status: e.target.value as PartStatus })} aria-label="Status">
                  {Object.entries(PART_STATUS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
              </TD>
              <TD>
                {canWork && (
                  <button className="text-muted-foreground hover:text-destructive" onClick={() => del.mutate(p.id)} aria-label="Teil entfernen">
                    <Trash2 className="size-4" />
                  </button>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {r.parts.length > 0 && <p className="border-t px-4 py-2 text-right text-sm text-muted-foreground">Materialwert (EK, netto): {formatCents(total)}</p>}
      {canWork && (
        <form
          className="grid gap-2 border-t p-3 sm:grid-cols-[1fr_9rem_9rem_5rem_7rem_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            if (f.description.trim()) add.mutate();
          }}
        >
          <Input placeholder="Bezeichnung" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} aria-label="Bezeichnung" />
          <Input placeholder="Teilenummer" value={f.partNumber} onChange={(e) => setF({ ...f, partNumber: e.target.value })} aria-label="Teilenummer" />
          <Input placeholder="Lieferant" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} aria-label="Lieferant" />
          <Input type="number" min={1} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} aria-label="Menge" />
          <Input inputMode="decimal" placeholder="EK €" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} aria-label="Einkaufspreis" />
          <Button type="submit" variant="outline" disabled={!f.description.trim()} loading={add.isPending}>
            <Plus /> Teil
          </Button>
        </form>
      )}
    </Card>
  );
}

type EditItem = QuoteItem & { price: string; qty: string };

/** „1,5“ → 1.5; leer/ungültig → null */
const parseQty = (s: string) => {
  const n = Number(s.trim().replace(',', '.'));
  return s.trim() && Number.isFinite(n) ? n : null;
};

/** Laufende Kosten (tatsächlicher Aufwand) gegenüber KVA bzw. Kostenfreigabe */
function CostStatusInfo({ r }: { r: RepairDetail }) {
  const c = r.costs;
  if (!c.reference) {
    return c.actualNet > 0 ? <p className="text-sm text-muted-foreground">Bisheriger Aufwand {formatCents(c.actualNet)} netto (kein freigegebener Kostenvoranschlag, keine Kostenfreigabe).</p> : null;
  }
  const isQuote = c.reference.kind === 'quote';
  const limit = c.reference.kind === 'quote' ? c.reference.net : c.reference.gross;
  const actual = isQuote ? c.actualNet : c.actualGross;
  const label = c.reference.kind === 'quote' ? `freigegebener Kostenvoranschlag (Fassung ${c.reference.version}) ${formatCents(limit)} netto` : `Kostenfreigabe ohne Rückfrage ${formatCents(limit)} brutto`;
  const pct = Math.min(100, Math.round((actual / Math.max(limit, 1)) * 100));
  return (
    <div className={cn('grid gap-1.5 rounded-md border p-3 text-sm', c.exceeded && 'border-destructive/50 bg-destructive/5')} role={c.exceeded ? 'alert' : undefined}>
      <div className="flex flex-wrap justify-between gap-2">
        <span>
          Bisheriger Aufwand <strong>{formatCents(actual)}</strong> {isQuote ? 'netto' : 'brutto'} · {label}
        </span>
        {c.exceeded && <Badge variant="destructive">überschritten um {formatCents(actual - limit)}</Badge>}
      </div>
      <div className="h-1.5 overflow-hidden rounded bg-muted">
        <div className={cn('h-full', c.exceeded ? 'bg-destructive' : pct > 85 ? 'bg-warning' : 'bg-primary')} style={{ width: `${pct}%` }} />
      </div>
      {c.exceeded && <p className="text-muted-foreground">Bitte mit dem Kunden abstimmen bzw. einen Nachtrag-Kostenvoranschlag senden. Abgerechnet wird der tatsächliche Aufwand.</p>}
    </div>
  );
}

function QuoteTab({ r, canQuote, onChange }: { r: RepairDetail; canQuote: boolean; onChange: () => void }) {
  const draft = r.quotes.find((q) => q.status === 'DRAFT');
  const pending = r.quotes.find((q) => q.status === 'SENT');
  const [items, setItems] = useState<EditItem[]>([]);
  const { data: rateData } = useQuery({ queryKey: ['hourly-rates'], queryFn: () => api<{ items: { id: string; name: string; priceCents: number; active: boolean }[] }>('/hourly-rates'), enabled: canQuote });
  const { data: myRates } = useMyRates();
  const { data: flatData } = useQuery({ queryKey: ['repair-flat-rates'], queryFn: () => api<{ items: { id: string; name: string; priceCents: number; active: boolean }[] }>('/repair-flat-rates'), enabled: canQuote });
  const rates = (rateData?.items ?? []).filter((x) => x.active);
  const flats = (flatData?.items ?? []).filter((x) => x.active);
  const defaultRate = rates.find((x) => x.id === myRates?.items.find((m) => m.isDefault)?.id) ?? rates[0];
  /** Preis je Einheit aus dem Stundensatz (bei Minuten anteilig) */
  const ratePrice = (rateId: string | null | undefined, unit: string) => {
    const rate = rates.find((x) => x.id === rateId);
    return rate ? centsToInput(unit === 'Min.' ? Math.round(rate.priceCents / 60) : rate.priceCents) : null;
  };
  const patchItem = (idx: number, p: Partial<EditItem>) => setItems((list) => list.map((x, j) => (j === idx ? { ...x, ...p } : x)));
  const changeKind = (idx: number, kind: QuoteItem['kind']) => {
    const unit = DEFAULT_QUOTE_UNIT[kind];
    if (kind === 'LABOR' && defaultRate) patchItem(idx, { kind, unit, hourlyRateId: defaultRate.id, flatRateId: null, price: ratePrice(defaultRate.id, unit) ?? '' });
    else patchItem(idx, { kind, unit, hourlyRateId: null, flatRateId: null });
  };
  const newItem = (): EditItem => ({ kind: 'LABOR', description: '', quantity: 1, qty: '1', unit: 'Std.', unitPriceCents: 0, hourlyRateId: defaultRate?.id ?? null, flatRateId: null, price: ratePrice(defaultRate?.id, 'Std.') ?? '' });
  const [taxRate, setTaxRate] = useState(19);
  const [note, setNote] = useState('');
  const [decision, setDecision] = useState<{ approved: boolean } | null>(null);
  const [decidedBy, setDecidedBy] = useState('');
  const [channel, setChannel] = useState<'PHONE' | 'IN_PERSON' | 'EMAIL'>('PHONE');
  const [decisionNote, setDecisionNote] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const editable = ['DIAGNOSIS', 'APPROVED', 'IN_REPAIR', 'WAITING_PARTS'].includes(r.status) && !pending;

  useEffect(() => {
    const base = draft?.items ?? [];
    setItems(base.map((i) => ({ ...i, qty: String(i.quantity).replace('.', ','), price: centsToInput(i.unitPriceCents) })));
    setTaxRate(draft?.taxRate ?? 19);
    setNote(draft?.note ?? '');
  }, [draft]);

  const onError = (e: unknown) => toast.error(errorMessage(e));
  const toBody = () => ({
    items: items.map((i) => ({
      kind: i.kind,
      description: i.description,
      quantity: parseQty(i.qty) ?? 0,
      unit: i.unit,
      unitPriceCents: parseEuro(i.price) ?? 0,
      hourlyRateId: i.kind === 'LABOR' ? i.hourlyRateId || null : null,
      flatRateId: i.kind === 'FLAT' ? i.flatRateId || null : null,
    })),
    taxRate,
    note: note || null,
  });
  const save = useMutation({ mutationFn: () => api(`/repairs/${r.id}/quote`, { method: 'PUT', body: toBody() }), onSuccess: () => { toast.success('Entwurf gespeichert'); onChange(); }, onError });
  const send = useMutation({
    mutationFn: async () => {
      await api(`/repairs/${r.id}/quote`, { method: 'PUT', body: toBody() });
      await api(`/repairs/${r.id}/quote/send`, { method: 'POST' });
    },
    onSuccess: () => {
      toast.success('Kostenvoranschlag an den Kunden gesendet');
      onChange();
    },
    onError,
  });
  const decide = useMutation({
    mutationFn: () => api(`/repairs/${r.id}/quote/decision`, { method: 'POST', body: { approved: decision!.approved, decidedBy, channel, note: decisionNote || null } }),
    onSuccess: () => {
      setDecision(null);
      onChange();
    },
    onError,
  });

  const net = items.reduce((s, i) => s + Math.round((parseQty(i.qty) ?? 0) * (parseEuro(i.price) ?? 0)), 0);
  const tax = Math.round((net * taxRate) / 100);
  const valid = items.length > 0 && items.every((i) => i.description.trim() && parseEuro(i.price) !== null && (parseQty(i.qty) ?? 0) > 0 && i.unit.trim());
  const fromParts = () =>
    setItems((x) => [
      ...x,
      ...r.parts.map((p) => ({ kind: 'PART' as const, description: p.description, quantity: p.quantity, qty: String(p.quantity), unit: 'Stk.', unitPriceCents: p.unitPriceCents, hourlyRateId: null, flatRateId: null, price: p.unitPriceCents ? centsToInput(p.unitPriceCents) : '' })),
    ]);

  return (
    <div className="grid gap-4">
      {pending && (
        <Alert className="flex flex-wrap items-center justify-between gap-3">
          <span>
            Fassung {pending.version} über <strong>{formatCents(pending.totals.gross)}</strong> wurde am {formatDateTime(pending.sentAt)} gesendet und wartet auf die Entscheidung des Kunden.
          </span>
          {canQuote && (
            <span className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setDecision({ approved: true })}>
                <CheckCircle2 /> Freigabe erfassen
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDecision({ approved: false })}>
                <XCircle /> Ablehnung erfassen
              </Button>
            </span>
          )}
        </Alert>
      )}
      <CostStatusInfo r={r} />

      {canQuote && editable && !draft && r.quotes.length > 0 && !showEditor && (
        <Button variant="outline" size="sm" className="w-fit" onClick={() => setShowEditor(true)}>
          <Plus /> Nachtrag-Kostenvoranschlag erstellen
        </Button>
      )}
      {canQuote && editable && (draft || r.quotes.length === 0 || showEditor) && (
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-sm">{draft ? `Entwurf – Fassung ${draft.version}` : 'Neuer Kostenvoranschlag'}</CardTitle>
            {r.parts.length > 0 && (
              <Button size="sm" variant="ghost" onClick={fromParts}>
                <Package /> Ersatzteile übernehmen
              </Button>
            )}
          </CardHeader>
          <CardContent className="grid gap-2">
            {items.map((i, idx) => {
              const fromCatalog = (i.kind === 'LABOR' && Boolean(i.hourlyRateId)) || (i.kind === 'FLAT' && Boolean(i.flatRateId));
              return (
                <div key={idx} className="grid gap-2 rounded-md border p-2">
                  <div className="grid grid-cols-[minmax(0,1fr)_2.25rem] gap-2 sm:grid-cols-[9rem_minmax(0,1fr)_2.25rem]">
                    <Select value={i.kind} onChange={(e) => changeKind(idx, e.target.value as QuoteItem['kind'])} aria-label="Art" className="col-span-2 sm:col-span-1">
                      {(Object.keys(QUOTE_KIND_LABELS) as QuoteItem['kind'][]).map((k) => (
                        <option key={k} value={k}>
                          {QUOTE_KIND_LABELS[k]}
                        </option>
                      ))}
                    </Select>
                    <Input value={i.description} placeholder="Beschreibung" onChange={(e) => patchItem(idx, { description: e.target.value })} aria-label="Beschreibung" />
                    <Button variant="ghost" size="icon" onClick={() => setItems(items.filter((_, j) => j !== idx))} aria-label="Position entfernen">
                      <Trash2 />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-[minmax(0,1fr)_5rem_6rem_7rem]">
                    {i.kind === 'LABOR' ? (
                      <Select
                        className="col-span-3 sm:col-span-1"
                        value={i.hourlyRateId ?? ''}
                        onChange={(e) => patchItem(idx, { hourlyRateId: e.target.value || null, ...(e.target.value ? { price: ratePrice(e.target.value, i.unit) ?? i.price } : {}) })}
                        aria-label="Stundensatz"
                      >
                        <option value="">Ohne Stundensatz</option>
                        {rates.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name} ({formatCents(x.priceCents)}/h)
                          </option>
                        ))}
                      </Select>
                    ) : i.kind === 'FLAT' ? (
                      <Select
                        className="col-span-3 sm:col-span-1"
                        value={i.flatRateId ?? ''}
                        onChange={(e) => {
                          const f = flats.find((x) => x.id === e.target.value);
                          patchItem(idx, f ? { flatRateId: f.id, description: i.description.trim() ? i.description : f.name, price: centsToInput(f.priceCents), unit: i.unit || 'pausch.' } : { flatRateId: null });
                        }}
                        aria-label="Pauschale"
                      >
                        <option value="">Freie Pauschale</option>
                        {flats.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name} ({formatCents(x.priceCents)})
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <span className="hidden sm:block" />
                    )}
                    <Input inputMode="decimal" value={i.qty} onChange={(e) => patchItem(idx, { qty: e.target.value })} aria-label="Menge" placeholder="Menge" />
                    <Input
                      list="quote-units"
                      value={i.unit}
                      onChange={(e) => patchItem(idx, { unit: e.target.value, ...(i.kind === 'LABOR' && i.hourlyRateId ? { price: ratePrice(i.hourlyRateId, e.target.value) ?? i.price } : {}) })}
                      aria-label="Einheit"
                      placeholder="Einheit"
                      maxLength={20}
                    />
                    <Input
                      inputMode="decimal"
                      placeholder="Preis netto"
                      value={i.price}
                      readOnly={fromCatalog}
                      title={fromCatalog ? 'Preis aus dem Katalog' : undefined}
                      className={fromCatalog ? 'bg-muted' : undefined}
                      onChange={(e) => patchItem(idx, { price: e.target.value })}
                      aria-label="Einzelpreis netto"
                    />
                  </div>
                </div>
              );
            })}
            <datalist id="quote-units">
              {QUOTE_UNITS.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
            <Button variant="outline" size="sm" className="w-fit" onClick={() => setItems([...items, newItem()])}>
              <Plus /> Position
            </Button>
            <div className="grid gap-3 pt-2 sm:grid-cols-[1fr_auto]">
              <Field label="Anmerkung für den Kunden" htmlFor="q-note">
                <Textarea id="q-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              <div className="grid content-end gap-1 text-right text-sm">
                <label className="flex items-center justify-end gap-2 text-muted-foreground">
                  USt.
                  <Input type="number" className="h-8 w-16" value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value))} aria-label="Steuersatz" /> %
                </label>
                <p className="text-muted-foreground">Netto {formatCents(net)}</p>
                <p className="text-muted-foreground">USt. {formatCents(tax)}</p>
                <p className="text-base font-semibold">Gesamt {formatCents(net + tax)}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => save.mutate()} disabled={!valid} loading={save.isPending}>
                Entwurf speichern
              </Button>
              <Button onClick={() => send.mutate()} disabled={!valid} loading={send.isPending}>
                <Send /> An Kunden senden
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {!editable && !pending && r.quotes.length === 0 && <p className="text-sm text-muted-foreground">In diesem Status ist kein Kostenvoranschlag möglich.</p>}

      {r.quotes
        .filter((q) => q.status !== 'DRAFT')
        .map((q) => (
          <Card key={q.id}>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-sm">
                Fassung {q.version} · {formatCents(q.totals.gross)}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant={q.status === 'APPROVED' ? 'success' : q.status === 'REJECTED' ? 'destructive' : 'warning'}>
                  {q.status === 'APPROVED' ? 'freigegeben' : q.status === 'REJECTED' ? 'abgelehnt' : 'offen'}
                </Badge>
                <a className="text-sm text-primary hover:underline" href={`/api/repairs/${r.id}/documents/quote?version=${q.version}`} target="_blank" rel="noreferrer">
                  PDF
                </a>
              </div>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              {q.items.map((i) => `${formatQuantity(i.quantity, i.unit)} ${i.description}`).join(' · ')}
              {q.decidedAt && (
                <p className="mt-1">
                  {q.status === 'APPROVED' ? 'Freigegeben' : 'Abgelehnt'} am {formatDateTime(q.decidedAt)} von {q.decidedBy} ({CHANNELS[q.decisionChannel as keyof typeof CHANNELS] ?? q.decisionChannel})
                  {q.decisionNote && ` – „${q.decisionNote}“`}
                </p>
              )}
              {q.consent && (
                <p className="mt-1 text-xs">
                  Bestätigt: {q.consent.documents.map((d) => `${d.title} (Fassung ${d.version})`).join(', ')}
                  {q.consent.ip && ` · IP ${q.consent.ip}`}
                </p>
              )}
            </CardContent>
          </Card>
        ))}

      <Dialog open={decision !== null} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{decision?.approved ? 'Freigabe erfassen' : 'Ablehnung erfassen'}</DialogTitle>
            <DialogDescription>Dokumentiert eine Entscheidung, die der Kunde außerhalb des Portals mitgeteilt hat.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Field label="Entschieden von" htmlFor="d-by">
              <Input id="d-by" value={decidedBy} onChange={(e) => setDecidedBy(e.target.value)} placeholder="Name des Kunden" />
            </Field>
            <Field label="Auf welchem Weg?" htmlFor="d-ch">
              <Select id="d-ch" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
                <option value="PHONE">telefonisch</option>
                <option value="IN_PERSON">persönlich</option>
                <option value="EMAIL">per E-Mail</option>
              </Select>
            </Field>
            <Field label="Bemerkung" htmlFor="d-note">
              <Input id="d-note" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)}>
              Abbrechen
            </Button>
            <Button onClick={() => decide.mutate()} disabled={decidedBy.trim().length < 2} loading={decide.isPending}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotosTab({ r, onChange }: { r: RepairDetail; onChange: () => void }) {
  const [visible, setVisible] = useState(true);
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('kind', 'WORK');
      form.append('customerVisible', String(visible));
      form.append('file', file);
      return api(`/repairs/${r.id}/attachments`, { method: 'POST', form });
    },
    onSuccess: onChange,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const kindLabel = (k: string) => (k === 'INTAKE' ? 'Annahme' : k === 'WORK' ? 'Werkstatt' : 'Sonstiges');
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <UploadButton onFile={(f) => upload.mutate(f)} loading={upload.isPending} label="Foto / Datei hochladen" />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={visible} onCheckedChange={(v) => setVisible(v === true)} /> für den Kunden sichtbar
          </label>
        </div>
        {r.attachments.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Fotos.</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {r.attachments.map((a) => {
            const url = `/api/repairs/${r.id}/attachments/${a.id}`;
            return (
              <a key={a.id} href={url} target="_blank" rel="noreferrer" className="group overflow-hidden rounded-lg border">
                {a.file.mimeType.startsWith('image/') ? (
                  <img src={url} alt={a.file.originalName} className="aspect-square w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                ) : (
                  <div className="flex aspect-square items-center justify-center bg-muted text-xs text-muted-foreground">{a.file.originalName}</div>
                )}
                <div className="flex items-center justify-between gap-1 p-2 text-xs text-muted-foreground">
                  <span>
                    {kindLabel(a.kind)} · {formatBytes(a.file.size)}
                  </span>
                  {a.customerVisible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                </div>
              </a>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function RepairDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [tab, setTab] = useState<'work' | 'parts' | 'quote' | 'photos' | 'history'>('work');
  const [readyOpen, setReadyOpen] = useState(false);
  const [outcome, setOutcome] = useState<RepairOutcome>('REPAIRED');
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [pickedUpBy, setPickedUpBy] = useState('');
  const [handoverNote, setHandoverNote] = useState('');
  const [code, setCode] = useState<string | null>(null);
  const [confirmDialog, confirm] = useConfirm();

  const key = ['repairs', 'detail', id];
  const { data: r, error } = useQuery({ queryKey: key, queryFn: () => api<RepairDetail>(`/repairs/${id}`) });
  const unlock = useUnlock(`/repairs/${id}`, r?.lock);
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta'), retry: false });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['repairs'] });
    qc.invalidateQueries({ queryKey: ['billing', 'ref'] });
  };
  const onError = (e: unknown) => toast.error(errorMessage(e));

  const setStatus = useMutation({
    mutationFn: (body: { status: RepairStatus; outcome?: RepairOutcome }) => api(`/repairs/${id}/status`, { method: 'POST', body }),
    onSuccess: () => {
      setReadyOpen(false);
      refresh();
    },
    onError,
  });
  const patch = useMutation({ mutationFn: (body: Record<string, unknown>) => api(`/repairs/${id}`, { method: 'PATCH', body }), onSuccess: refresh, onError });
  const doHandover = useMutation({
    mutationFn: () => api(`/repairs/${id}/handover`, { method: 'POST', body: { pickedUpBy, note: handoverNote || null } }),
    onSuccess: () => {
      setHandoverOpen(false);
      toast.success('Abholung dokumentiert');
      refresh();
      window.open(`/api/repairs/${id}/documents/pickup`, '_blank');
    },
    onError,
  });
  const reveal = useMutation({ mutationFn: () => api<{ code: string | null }>(`/repairs/${id}/access-code`), onSuccess: (d) => setCode(d.code ?? ''), onError });

  if (error instanceof ApiError && error.status === 404) return <p className="py-20 text-center text-muted-foreground">Reparatur nicht gefunden.</p>;
  if (!r) return <Skeleton className="h-96" />;

  const editable = !r.lock.kind || (r.lock.canOverride && unlock.unlocked);
  const canWork = can('repairs.work') && editable;
  const documents = ['intake', 'label', 'workshop', ...(r.quotes.length ? ['quote'] : []), 'report', ...(r.status === 'PICKED_UP' ? ['pickup'] : [])];

  return (
    <>
      {confirmDialog}
      <Link to="/repairs" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Reparaturen
      </Link>
      {params.get('new') && (
        <Alert variant="success" className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <span>Gerät angenommen. Drucken Sie jetzt Annahmeschein und Etikett.</span>
          <span className="flex gap-2">
            <Button asChild size="sm" variant="outline">
              <a href={`/api/repairs/${r.id}/documents/intake`} target="_blank" rel="noreferrer">
                <Printer /> Annahmeschein
              </a>
            </Button>
            <Button asChild size="sm" variant="outline">
              <a href={`/api/repairs/${r.id}/documents/label`} target="_blank" rel="noreferrer">
                <Printer /> Etikett
              </a>
            </Button>
          </span>
        </Alert>
      )}

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{r.number}</span>
            <RepairStatusBadge status={r.status} />
            {r.outcome && <Badge variant="outline">{OUTCOMES[r.outcome]}</Badge>}
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{r.device.label}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <Link to={`/customers/${r.customer.id}`} className="hover:underline">
              {r.customer.name}
            </Link>{' '}
            · angenommen {formatDateTime(r.receivedAt)}
            {r.ticket && (
              <>
                {' '}
                · Ticket{' '}
                <Link to={`/tickets/${r.ticket.id}`} className="hover:underline">
                  {r.ticket.number}
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWork &&
            r.allowedTransitions
              .filter((s) => s !== 'CANCELLED' || can('repairs.intake'))
              .map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={s === 'READY_FOR_PICKUP' ? 'default' : 'outline'}
                  loading={setStatus.isPending && setStatus.variables?.status === s}
                  onClick={async () => {
                    if (s === 'READY_FOR_PICKUP') return setReadyOpen(true);
                    if (s === 'CANCELLED' && !(await confirm({ title: 'Reparatur stornieren?', description: 'Nur für Fehleingaben. Das Gerät muss trotzdem zurückgegeben werden.', confirmLabel: 'Stornieren', destructive: true }))) return;
                    setStatus.mutate({ status: s });
                  }}
                >
                  {s === 'READY_FOR_PICKUP' ? 'Fertig – abholbereit' : `→ ${REPAIR_STATUS[s].label}`}
                </Button>
              ))}
          {r.status === 'READY_FOR_PICKUP' && can('repairs.handover') && (
            <Button size="sm" onClick={() => setHandoverOpen(true)}>
              <Package /> Abholung
            </Button>
          )}
          <DocumentsMenu repairId={r.id} available={documents} />
          <PrintButton documents={DOCUMENTS.filter((d) => documents.includes(d.type)).map((d) => ({ key: d.type, label: d.title }))} target={{ repairId: r.id }} />
        </div>
      </div>

      {r.readyPendingAt && (
        <Alert variant="success" className="mb-4">
          Fertig gemeldet am {formatDateTime(r.readyPendingAt)} – das Gerät wird automatisch abholbereit (mit Kundenbenachrichtigung), sobald die Rechnung erstellt ist.
        </Alert>
      )}
      <LockBanner lock={r.lock} unlock={unlock} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] 3xl:grid-cols-[minmax(0,1fr)_24rem] 4xl:grid-cols-[minmax(0,1fr)_50rem]">
        <div className="min-w-0">
          <Card className="mb-4">
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">Fehlerbeschreibung</p>
              <p className="whitespace-pre-wrap text-sm">{r.faultDescription}</p>
            </CardContent>
          </Card>
          <Tabs
            value={tab}
            onChange={setTab}
            items={[
              { value: 'work', label: 'Werkstatt' },
              { value: 'parts', label: `Ersatzteile (${r.parts.length})` },
              { value: 'quote', label: 'Kostenvoranschlag' },
              { value: 'photos', label: `Fotos (${r.attachments.length})` },
              { value: 'history', label: 'Verlauf' },
            ]}
          />
          <div className="pt-4">
            {tab === 'work' && <StepsTab r={r} canWork={canWork} onChange={refresh} />}
            {tab === 'parts' && <PartsTab r={r} canWork={canWork} onChange={refresh} />}
            {tab === 'quote' && <QuoteTab r={r} canQuote={can('repairs.quotes')} onChange={refresh} />}
            {tab === 'photos' && <PhotosTab r={r} onChange={refresh} />}
            {tab === 'history' && (
              <Card>
                <CardContent className="grid gap-2 pt-5">
                  {r.events.map((e) => (
                    <p key={e.id} className="text-sm">
                      <span className="font-medium">{e.actorName ?? 'System'}</span> {describe(e.type, e.data)}{' '}
                      <span className="text-xs text-muted-foreground">· {formatDateTime(e.createdAt)}</span>
                    </p>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Rechte Spalte: einklappbare Karten, auf großen Bildschirmen eigenständig scrollbar */}
        <aside className="lg:sticky lg:top-18 lg:max-h-[calc(100dvh-5.5rem)] lg:self-start lg:overflow-y-auto lg:pr-1">
          <CollapsibleCards scope="repair" defaultOpen={['Kunde', 'Gerät', 'Pauschalen', 'Abrechnung']} className="flex flex-col gap-4 4xl:grid 4xl:grid-cols-2 4xl:content-start">
          <Card>
            <CardContent className="grid gap-3 pt-5">
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">Techniker</Label>
                <Select value={r.technician?.id ?? ''} disabled={!canWork} onChange={(e) => patch.mutate({ technicianId: e.target.value || null })}>
                  <option value="">– nicht zugewiesen –</option>
                  {meta?.agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">Voraussichtlich fertig</Label>
                <Input
                  type="date"
                  disabled={!canWork}
                  defaultValue={r.estimatedCompletionAt?.slice(0, 10) ?? ''}
                  key={r.estimatedCompletionAt ?? 'none'}
                  onBlur={(e) => patch.mutate({ estimatedCompletionAt: e.target.value ? new Date(`${e.target.value}T17:00:00`).toISOString() : null })}
                />
              </div>
              <dl className="grid gap-2 border-t pt-3">
                <Info label="Zubehör">{r.accessories.length ? r.accessories.join(', ') : 'ohne'}</Info>
                {r.condition && <Info label="Optischer Zustand">{r.condition}</Info>}
                <Info label="Abrechnung">
                  {WARRANTIES[r.warrantyType]}
                  {r.warrantyNote && <span className="block text-xs text-muted-foreground">{r.warrantyNote}</span>}
                </Info>
                <Info label="Kostenfreigabe bis">{r.costLimitCents != null ? formatCents(r.costLimitCents) : 'nur nach KVA'}</Info>
                {r.hasAccessCode && canWork && (
                  <Info label="Gerätecode">
                    {code !== null ? (
                      <span className="font-mono">{code}</span>
                    ) : (
                      <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => reveal.mutate()}>
                        <KeyRound className="size-3.5" /> anzeigen (wird protokolliert)
                      </button>
                    )}
                  </Info>
                )}
                {r.pickedUpAt && (
                  <Info label="Abgeholt">
                    {formatDateTime(r.pickedUpAt)} von {r.pickedUpBy}
                  </Info>
                )}
              </dl>
            </CardContent>
          </Card>
          <AppointmentsCard
            repairId={r.id}
            defaults={{
              title: `Reparatur ${r.number}`,
              customerId: r.customer.id,
              customerEmails: r.contact?.email ? [r.contact.email] : [],
            }}
          />
          <PublicStatusCard repairId={r.id} status={r.publicStatus} onChange={refresh} />
          <RepairChargesCard repairId={r.id} locked={!editable} />
          <BillingCard repairId={r.id} repairStatus={r.status} repairOutcome={r.outcome} />
          <ChecklistsCard repairId={r.id} />
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Kunde</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-1 text-sm">
              <Link to={`/customers/${r.customer.id}`} className="font-medium hover:underline">
                {r.customer.name}
              </Link>
              {r.contact && <span>{r.contact.name}</span>}
              {(r.contact?.phone ?? r.customerPhone) && <a href={`tel:${r.contact?.phone ?? r.customerPhone}`} className="text-primary">{r.contact?.phone ?? r.customerPhone}</a>}
              {r.contact?.email && <span className="text-muted-foreground">{r.contact.email}</span>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Gerät</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm">
              <Link to={`/devices/${r.deviceInfo.id}`} className="font-medium hover:underline">
                {r.device.label}
              </Link>
              {r.deviceInfo.serialNumber && <span className="font-mono text-xs">SN {r.deviceInfo.serialNumber}</span>}
              {r.deviceInfo.inventoryNumber && <span className="text-xs text-muted-foreground">Inventar {r.deviceInfo.inventoryNumber}</span>}
              {r.deviceHistory.length > 0 && (
                <div className="mt-2 border-t pt-2">
                  <p className="mb-1 text-xs text-muted-foreground">Frühere Reparaturen</p>
                  {r.deviceHistory.map((h) => (
                    <button key={h.id} onClick={() => navigate(`/repairs/${h.id}`)} className="block w-full truncate text-left text-xs hover:underline">
                      <span className="font-mono">{h.number}</span> · {formatDate(h.receivedAt)} · {h.faultDescription}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          </CollapsibleCards>
        </aside>
      </div>

      <Dialog open={readyOpen} onOpenChange={setReadyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Als abholbereit markieren</DialogTitle>
            <DialogDescription>Ohne Kosten sofort abholbereit (der Kunde wird benachrichtigt). Mit Kosten geht der Vorgang zuerst an die Buchhaltung; nach der Rechnung wird das Gerät automatisch abholbereit.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Field label="Ergebnis" htmlFor="o-res">
              <Select id="o-res" value={outcome} onChange={(e) => setOutcome(e.target.value as RepairOutcome)}>
                {Object.entries(OUTCOMES).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReadyOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => setStatus.mutate({ status: 'READY_FOR_PICKUP', outcome })} loading={setStatus.isPending}>
              Abholbereit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={handoverOpen} onOpenChange={setHandoverOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abholung dokumentieren</DialogTitle>
            <DialogDescription>Danach wird der Abholbeleg zum Unterschreiben geöffnet.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Field label="Abgeholt von" htmlFor="h-by">
              <Input id="h-by" value={pickedUpBy} onChange={(e) => setPickedUpBy(e.target.value)} placeholder="Name der abholenden Person" autoFocus />
            </Field>
            <Field label="Bemerkung" htmlFor="h-note">
              <Input id="h-note" value={handoverNote} onChange={(e) => setHandoverNote(e.target.value)} placeholder="z. B. bar bezahlt, Rechnung folgt" />
            </Field>
            <p className="text-sm text-muted-foreground">Zubehör zurückgeben: {r.accessories.length ? r.accessories.join(', ') : 'ohne'}</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoverOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => doHandover.mutate()} disabled={pickedUpBy.trim().length < 2} loading={doHandover.isPending}>
              Abholung bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
